import { FC } from 'react';
import { t } from 'i18next';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Modal,
  Typography,
} from '@mui/material';
import { Clear, FileCopy, Delete, Upload } from '@mui/icons-material';

import { theme } from '@store';

import { FilePreviewContent } from './components';
import { fileFormatMapper } from './fileFormatMapper';

export interface FilePreviewContent {
  type: string;
  content: string;
}

interface FileDetails {
  file_name: string;
  size: string;
  file_format: string;
  id: string;
}

interface FilePreviewProps {
  isOpen: boolean;
  handleClose: () => void;
  fileDetails: FileDetails | null;
  file: FilePreviewContent | null;
  isFileDownloading: boolean;
  isReadOnly: boolean;
  handleDownload?: () => void;
  handleDelete?: (value: FileDetails) => void;
}

export const FilePreview: FC<FilePreviewProps> = ({
  isOpen,
  handleClose,
  fileDetails,
  file,
  isFileDownloading,
  handleDownload,
  handleDelete,
  isReadOnly = false,
}) => {
  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Box>
        <Box
          sx={{
            display: 'flex',
            p: '8px 24px',
            alignItems: 'center',
            background: 'rgba(0, 0, 0, 0.50)',
            backdropFilter: 'blur(2px)',
            color: theme.palette.common.white,
          }}
          onClick={e => e.stopPropagation()}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              flex: 1,
            }}
          >
            <FileCopy
              sx={{ color: theme.palette.common.white, height: 34, width: 34 }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography
                variant='body1'
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  width: '33vw',
                }}
              >
                {fileDetails?.file_name}
              </Typography>
              <Typography variant='caption'>{fileDetails?.size}</Typography>
            </Box>
          </Box>

          <Box
            sx={{ display: 'flex', gap: 3, flex: 1, justifyContent: 'center' }}
          >
            {handleDownload && (
              <Button
                sx={{ color: theme.palette.common.white }}
                onClick={handleDownload}
                startIcon={<Upload sx={{ height: 24, width: 24 }} />}
              >
                {t('common.filePreview.download')}
              </Button>
            )}
            {handleDelete && (
              <Button
                sx={{ color: theme.palette.common.white }}
                onClick={() => {
                  if (fileDetails) {
                    handleDelete(fileDetails);
                    handleClose();
                  }
                }}
                startIcon={<Delete sx={{ height: 24, width: 24 }} />}
                disabled={isReadOnly}
              >
                {t('common.filePreview.delete')}
              </Button>
            )}
          </Box>

          <IconButton
            onClick={handleClose}
            sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}
          >
            <Clear
              sx={{ color: theme.palette.common.white, height: 24, width: 24 }}
            />
          </IconButton>
        </Box>
        <Box
          sx={{
            display: 'grid',
            placeItems: 'center',
            height: '100vh',
            width: '100vw',
          }}
        >
          {isFileDownloading ? (
            <Box
              sx={{
                display: 'grid',
                placeItems: 'center',
                height: 400,
                width: 400,
                background: theme.palette.common.white,
                border: '1px solid rgba(0, 0, 0, 012)',
              }}
            >
              <CircularProgress size='64px' />
            </Box>
          ) : (
            <>
              {file && (
                <FilePreviewContent
                  src={file.content}
                  title='preview image'
                  type={fileFormatMapper(fileDetails?.file_format ?? '')}
                  handleClose={handleClose}
                />
              )}
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
};
